import * as React from 'react'
import useFetch, { CachePolicies } from 'use-http'
import { useDebounce } from '../hooks/useDebounce'

interface UserPreferencesContextValue {
  prefs: UserPreferences
  setPreviewVisible(visible: boolean): void
  setLastTab(key: TabKey): void
}

const DEFAULT_PREFS: UserPreferences = {
  previewVisible: false,
  lastTab: 'summary',
}

function fromApiPrefs(
  api: UserPreferences | null | undefined,
): UserPreferences {
  if (!api) {
    return DEFAULT_PREFS
  }
  return {
    previewVisible: api.previewVisible ?? DEFAULT_PREFS.previewVisible,
    lastTab: api.lastTab ?? DEFAULT_PREFS.lastTab,
  }
}

interface UserPreferencesProviderProps {
  userId: string | undefined
  children: React.ReactNode
}

export const UserPreferencesProvider: React.FC<
  UserPreferencesProviderProps
> = ({ userId, children }) => {
  const [prefs, setPrefs] = React.useState<UserPreferences>(DEFAULT_PREFS)
  const debouncedPrefs = useDebounce(prefs, 500)

  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  // Initial load from API
  React.useEffect(() => {
    let cancelled = false

    async function load(): Promise<void> {
      try {
        const data = (await request.get(
          `/api/user/${userId}/preferences`,
        )) as UserPreferences | null

        if (cancelled) {
          return
        }

        const next = fromApiPrefs(data)
        setPrefs(next)
      } catch {
        // On error, just keep whatever is in state (DEFAULT_PREFS initially)
      }
    }

    if (userId) {
      void load()
    }

    return () => {
      cancelled = true
    }
  }, [userId])

  // Debounced sync to server
  React.useEffect(() => {
    if (!userId) {
      return
    }

    void request.patch(`/api/user/${userId}/preferences`, {
      previewVisible: debouncedPrefs.previewVisible,
      lastTab: debouncedPrefs.lastTab,
    })
  }, [debouncedPrefs, userId])

  const setPreviewVisible = React.useCallback((visible: boolean) => {
    setPrefs(curr => ({
      ...curr,
      previewVisible: visible,
    }))
  }, [])

  const setLastTab = React.useCallback((key: TabKey) => {
    setPrefs(curr => ({
      ...curr,
      lastTab: key,
    }))
  }, [])

  const value = React.useMemo(
    () => ({
      prefs,
      setPreviewVisible,
      setLastTab,
    }),
    [prefs, setPreviewVisible, setLastTab],
  )

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  )
}

export function useUserPreferences(): UserPreferencesContextValue {
  const context = React.useContext(UserPreferencesContext)
  if (!context) {
    throw new Error(
      'useUserPreferences must be used within UserPreferencesProvider',
    )
  }
  return context
}

export type TabKey = 'summary' | 'customMetadata' | 'history' | 'comments'

export interface UserPreferences {
  previewVisible: boolean
  lastTab: TabKey
}

export const UserPreferencesContext =
  React.createContext<UserPreferencesContextValue | null>(null)
